import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import VeggieIcon from "@/components/VeggieIcon";

/**
 * Internal preview page — `/preview/veggies` — showing the difference
 * between the current flat-SVG VeggieIcon and the proposed painterly
 * watercolour illustrations generated via Gemini Nano Banana.
 *
 * The painterly samples live in `/public/brand/sample-veggies/` and are
 * generated by `backend/tools/sample_veggie_illustrations.py`. The page is
 * deliberately not linked from the main nav — it's a one-off comparison
 * surface so the brand owner can decide whether to commit to bulk
 * generation across the full crop catalog.
 */

const SAMPLES = [
    { slug: "tomato-fruiting", name: "Tomato", stage: "fruiting", caption: "Fruiting stage — ripening on the vine" },
    { slug: "carrot-mature", name: "Carrot", stage: "harvest", caption: "Mature root — ready for harvest" },
    { slug: "lettuce-growing", name: "Lettuce", stage: "vegetative", caption: "Vegetative stage — leafing out" },
    { slug: "basil-mature", name: "Basil", stage: "fruiting", caption: "Mature herb — ready to pinch" },
];

export default function VeggiePreview() {
    return (
        <div className="min-h-screen paper-texture pb-20" data-testid="veggie-preview-page">
            <div className="max-w-6xl mx-auto px-6 pt-8">
                <div className="flex items-center justify-between mb-10">
                    <Link to="/about">
                        <Button variant="ghost" data-testid="preview-back" className="text-[var(--vm-text-muted)]">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back
                        </Button>
                    </Link>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[var(--vm-border)] bg-[var(--vm-card)]/60 text-[10px] uppercase tracking-[0.18em] text-[var(--vm-sage)]">
                        <Sparkles className="w-3 h-3" /> Visual style preview
                    </span>
                </div>

                <header className="max-w-2xl mb-12">
                    <h1 className="font-serif-display text-5xl text-[var(--vm-text)] leading-tight">
                        Painterly watercolour vs. flat-SVG
                    </h1>
                    <p className="text-base text-[var(--vm-text-secondary)] mt-4 leading-relaxed">
                        Below is a side-by-side comparison so you can decide which feels right for the bed grid.
                        Left column = current flat icons. Right column = proposed Gemini Nano Banana watercolour
                        illustrations (generated once, cached forever — no per-render cost after bulk-bake).
                    </p>
                </header>

                <div className="grid md:grid-cols-2 gap-5 mb-16">
                    <div className="text-xs uppercase tracking-[0.22em] text-[var(--vm-sage)] mb-2">
                        Current — flat SVG
                    </div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[var(--vm-accent)] mb-2">
                        Proposed — painterly watercolour
                    </div>

                    {SAMPLES.map((s) => (
                        <div key={s.slug} className="contents" data-testid={`compare-${s.slug}`}>
                            {/* Current */}
                            <div className="journal-card p-6 flex flex-col items-center justify-center gap-3">
                                <div className="w-40 h-40 flex items-center justify-center bg-[var(--vm-input)]/40 rounded-lg border border-[var(--vm-border)]">
                                    <VeggieIcon cropName={s.name.toLowerCase()} stage={s.stage} size={120} svgOnly />
                                </div>
                                <div className="text-center">
                                    <div className="font-medium text-[var(--vm-text)]">{s.name}</div>
                                    <div className="text-xs text-[var(--vm-text-muted)] mt-1">{s.caption}</div>
                                </div>
                            </div>
                            {/* Proposed */}
                            <div className="journal-card p-6 flex flex-col items-center justify-center gap-3 border-[var(--vm-accent)]/40">
                                <div className="w-40 h-40 rounded-lg overflow-hidden border border-[var(--vm-accent)]/40 shadow-sm">
                                    <img
                                        src={`/brand/sample-veggies/${s.slug}.png`}
                                        alt={`${s.name} watercolour`}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="text-center">
                                    <div className="font-medium text-[var(--vm-text)]">{s.name}</div>
                                    <div className="text-xs text-[var(--vm-text-muted)] mt-1">{s.caption}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* In-context preview — grid cells */}
                <header className="max-w-2xl mb-6">
                    <h2 className="font-serif-display text-3xl text-[var(--vm-text)]">
                        How they'd read in the bed grid
                    </h2>
                    <p className="text-sm text-[var(--vm-text-secondary)] mt-2 leading-relaxed">
                        At cell-size — closer to how you'd actually see them in the Bed Designer.
                    </p>
                </header>

                <div className="grid grid-cols-2 gap-4">
                    <div className="journal-card p-5">
                        <div className="text-xs uppercase tracking-[0.22em] text-[var(--vm-sage)] mb-3">Flat SVG · 4 cells</div>
                        <div className="grid grid-cols-4 gap-2">
                            {SAMPLES.map((s) => (
                                <div key={s.slug} className="aspect-square rounded-lg bg-[var(--vm-input)]/40 border border-[var(--vm-border)] flex items-center justify-center">
                                    <VeggieIcon cropName={s.name.toLowerCase()} stage={s.stage} size={42} svgOnly />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="journal-card p-5 border-[var(--vm-accent)]/40">
                        <div className="text-xs uppercase tracking-[0.22em] text-[var(--vm-accent)] mb-3">Watercolour · 4 cells</div>
                        <div className="grid grid-cols-4 gap-2">
                            {SAMPLES.map((s) => (
                                <div key={s.slug} className="aspect-square rounded-lg overflow-hidden border border-[var(--vm-accent)]/30">
                                    <img src={`/brand/sample-veggies/${s.slug}.png`} alt={s.name} className="w-full h-full object-cover" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-12 journal-card p-6 max-w-3xl">
                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">If you like this style</h3>
                    <ul className="text-sm text-[var(--vm-text-secondary)] mt-3 space-y-2 leading-relaxed list-disc pl-5">
                        <li>I'll bulk-generate <strong>~60 crops × 4 stages = ~240 images</strong> in one run (€2-3 of Nano Banana credit).</li>
                        <li>Each illustration is cached in MongoDB / served from <code>/brand/veggies/&lbrace;crop&rbrace;-&lbrace;stage&rbrace;.png</code> — no recurring API cost.</li>
                        <li>The <code>VeggieIcon</code> component will swap out for an <code>&lt;img&gt;</code> when an illustration exists, otherwise fall back to flat SVG.</li>
                        <li>Optional <strong>underground toggle</strong> shows root-system illustrations side-by-side for an educational depth view.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
